import { IStyleSheetConfig } from "@fluentui/react";

/**
 * The cookie consent service that displays the consent banner and options
 */
export interface ICookieConsentService {
  /**
   * Initializes the cookie consent service and displays the banner if needed
   *
   * @param language - the user's language
   * @param elementId - the element ID
   */
  initialize(language: string, elementId: string): Promise<void>;

  /**
   * Initiates the consent management dialog
   */
  manageConsent(): void;

  /**
   * Gets a value indicating whether consent is required.
   * This would be false in markets without GDPR regulations
   *
   * @returns true if required
   */
  consentRequired(): boolean;
}

/**
 * The cookie consent service that displays the banner and options
 */
export class CookieConsentService implements ICookieConsentService {
  /**
   * The site consent object
   */
  private siteConsent: WcpConsent.SiteConsent;

  /**
   * The nonce for CSP support
   */
  private nonce: string;

  /**
   * Initializes a new instance of the CookieConsentService class.
   */
  public constructor() {
    const config = CookieConsentService.getFabricConfig();
    this.nonce = config.FabricConfig?.mergeStyles?.cspSettings?.nonce ?? "";
  }

  /**
   * Initializes the cookie consent service and displays the banner if needed
   *
   * @param language - the user's language
   * @param elementId - the element ID
   * @returns a promise to represent the async operation
   */
  public initialize(language: string, elementId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      window.WcpConsent &&
        WcpConsent.init(
          language,
          elementId,
          (err, _siteConsent) => {
            if (err) {
              reject(err);
            } else if (!_siteConsent) {
              reject(new Error("_siteConsent was null after initialization"));
            } else {
              // Save the reference to the site consent object
              this.siteConsent = _siteConsent;
              resolve();
            }
          },
          undefined,
          undefined,
          this.nonce
        );
    });
  }

  /**
   * Initiates the consent management dialog
   */
  public manageConsent(): void {
    this.siteConsent?.manageConsent();
  }

  /**
   * Gets a value indicating whether consent is required.
   * This would be false in markets without GDPR regulations
   *
   * @returns true if required
   */
  public consentRequired(): boolean {
    return this.siteConsent?.isConsentRequired ?? true;
  }

  /**
   * Gets the fabric window configuration
   *
   * @returns - The global fabric configuration
   */
  private static getFabricConfig() {
    let global: (Window | Record<string, unknown>) & {
      /**
       * The fabric config object
       */
      FabricConfig?: {
        /**
         * The style sheet configuration configuration
         */
        mergeStyles?: IStyleSheetConfig;
      };
    } = {};

    // Grab window.
    try {
      global = window || {};
    } catch {
      /* leave as blank object */
    }

    return global;
  }
}
